import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SERIES = [
  { key: "finishing", label: "Finishing",    stroke: "#3B82F6" },
  { key: "defending", label: "Defending",    stroke: "#EF4444" },
  { key: "passing",   label: "Passing Game", stroke: "#EAB308" },
  { key: "overall",   label: "Overall",      stroke: "#10B981" },
];

function shortDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch { return iso; }
}

function longDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const isStart = row.__isStart;
  return (
    <div
      data-testid="timeline-tooltip"
      className="rounded-md border border-border bg-popover/95 backdrop-blur px-3.5 py-3 shadow-lg text-xs min-w-[220px] max-w-[300px]"
    >
      <div className="font-display uppercase tracking-[0.18em] text-[10px] text-muted-foreground mb-1.5">
        {isStart ? "Season start" : longDate(row.match_date)}
      </div>
      {!isStart && (
        <div className="space-y-0.5 mb-2">
          <div className="font-medium text-foreground">
            {row.team_name || "—"} <span className="text-muted-foreground">vs</span> {row.opponent_name || "—"}
          </div>
          {row.competition_name && (
            <div className="text-[11px] text-muted-foreground truncate">{row.competition_name}</div>
          )}
          {(row.home_score != null || row.away_score != null) && (
            <div className="font-mono text-[11px] text-muted-foreground">
              Score: {row.home_score ?? "?"} – {row.away_score ?? "?"}
              {row.minutes_played != null && <span className="ml-2">· {row.minutes_played} min</span>}
            </div>
          )}
        </div>
      )}
      <div className="space-y-1 border-t border-border pt-1.5">
        {SERIES.map((s) => (
          <div key={s.key} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-[2px]" style={{ background: s.stroke }} />
              <span className="text-foreground">{s.label}</span>
            </span>
            <span className="font-mono text-foreground/80">{row[s.key] != null ? `${Number(row[s.key]).toFixed(1)}%` : "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomLegend() {
  return (
    <ul
      data-testid="timeline-legend"
      className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-2 pb-1 pt-2"
    >
      {SERIES.map((s) => (
        <li key={s.key} className="flex items-center gap-2 text-xs font-display uppercase tracking-[0.14em]">
          <span className="inline-block h-2.5 w-3.5 rounded-[2px]" style={{ background: s.stroke }} />
          <span className="text-foreground/80">{s.label}</span>
        </li>
      ))}
    </ul>
  );
}

export default function TimelineChart({ playerId, playerName }) {
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [season, setSeason] = useState(null);  // null = let backend pick the most recent
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    const params = season ? { season } : {};
    api.get(`/players/${playerId}/timeline`, { params })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Failed to load timeline"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [playerId, season, ignoreFriendlies]);

  // When the player or the friendlies setting changes, reset selected season
  // so the backend picks the freshest one from the (re)filtered dataset.
  useEffect(() => { setSeason(null); }, [playerId, ignoreFriendlies]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const sp = data.starting_point || {};
    const start = {
      __isStart: true,
      match_date: data.season_start_date,
      overall: sp.overall, finishing: sp.finishing, passing: sp.passing, defending: sp.defending,
      fbm_score: sp.fbm_score, minutes_played: sp.minutes_played,
    };
    return [start, ...(data.points || [])];
  }, [data]);

  const seasonLabel = data?.season || season || "—";
  const availableSeasons = data?.available_seasons || (season ? [season] : []);

  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="timeline-chart">
      <header className="px-6 pt-6 pb-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Match by match · starts at 50/50/50/50</div>
          <h2 className="font-display uppercase tracking-tight text-xl" data-testid="timeline-card-title">
            {playerName ? `${playerName}'s Season Timeline` : "Season Timeline"} · <span className="text-muted-foreground">{seasonLabel}</span>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Season</span>
          <Select
            value={data?.season || ""}
            onValueChange={(v) => setSeason(v)}
            disabled={loading || !availableSeasons.length}
          >
            <SelectTrigger className="h-9 min-w-[120px]" data-testid="timeline-season-select">
              <SelectValue placeholder={loading ? "Loading…" : "Pick season"} />
            </SelectTrigger>
            <SelectContent>
              {availableSeasons.map((s) => (
                <SelectItem key={s} value={s} data-testid={`timeline-season-option-${s.replace("/", "-")}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="px-2 pt-2 pb-4">
        <div style={{ width: "100%", height: 380, minHeight: 380 }} data-testid="timeline-chart-canvas">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="timeline-loading">
              Loading season…
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="timeline-error">
              {error}
            </div>
          ) : !data || (data.points?.length ?? 0) === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-muted-foreground gap-1" data-testid="timeline-empty">
              <div className="font-display uppercase tracking-[0.18em] text-xs">No matches in this season</div>
              {data?.season_start_date && (
                <div className="text-[11px]">{longDate(data.season_start_date)} → {longDate(data.season_end_date)}</div>
              )}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%" minWidth={320} minHeight={320}>
              <LineChart data={chartData} margin={{ top: 18, right: 28, left: 8, bottom: 26 }}>
                <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} />
                <XAxis
                  dataKey="match_date"
                  tickFormatter={shortDate}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.55 }}
                  tickLine={false}
                  axisLine={{ stroke: "currentColor", strokeOpacity: 0.15 }}
                  minTickGap={28}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.55 }}
                  tickLine={false}
                  axisLine={{ stroke: "currentColor", strokeOpacity: 0.15 }}
                  width={32}
                  tickFormatter={(v) => `${v}`}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: "currentColor", strokeOpacity: 0.18, strokeDasharray: "3 3" }} />
                <Legend content={<CustomLegend />} verticalAlign="top" wrapperStyle={{ paddingBottom: 6 }} />
                {SERIES.map((s) => (
                  <Line
                    key={s.key}
                    type="linear"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.stroke}
                    strokeWidth={2}
                    dot={{ r: 3, strokeWidth: 0, fill: s.stroke }}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--background, #fff)", fill: s.stroke }}
                    isAnimationActive
                    animationDuration={500}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </section>
  );
}
